<template>
  <v-row>
    <v-col>
      <div v-if="facilityData" class="facility">
        <IsOwnerIndicator :isOwner="isOwner" height="24px" class="mx-3" />
        <div class="facility__content-primary">
          <p class="facility__name">{{ facilityName }}</p>
          <div class="facility__description">
            <p class="facility__address">
              {{ facilityDescription }}
            </p>
          </div>
        </div>
      </div>
      <div class="changes" v-if="changes.length > 0">
        <div class="changes__list-header">
          <v-row>
            <v-col cols="2">
              {{ $t("message.externalChanges.date") }}
            </v-col>
            <v-col cols="2">
              {{ $t("message.externalChanges.origin") }}
            </v-col>

            <v-col cols="5">
              {{ $t("message.externalChanges.change") }}
            </v-col>

            <v-col cols="3"> </v-col>
          </v-row>
        </div>
        <div class="changes__list">
          <ChangeListItem
            v-for="change in changes"
            :key="change.id"
            :change="change"
            @accept="
              handleAction(
                $event,
                EXTERNAL_CHANGES_STATUS.ACCEPTED,
                change.field.value
              )
            "
            @reject="
              handleAction(
                $event,
                EXTERNAL_CHANGES_STATUS.REJECTED,
                change.field.value
              )
            "
            class="changes__list-item"
          />
        </div>
      </div>
      <div class="no-changes" v-else-if="changes.length === 0 && loaded">
        <img
          src="@/static/externalChanges/noChanges.svg"
          :alt="$t('message.externalChanges.noChangesAlt')"
        />
        <p class="no-groups__description">
          {{ $t("message.externalChanges.noChanges") }}
        </p>
      </div>
    </v-col>
    <SectionLoader :status="loading.view" :opacity="1" />
  </v-row>
</template>

<script>
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";
import ChangeListItem from "@/components/ExternalChanges/ChangeListItem.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";

import { EXTERNAL_CHANGES_STATUS } from "@/enums/external-changes";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { gtmEvent__singleExternalChangeAction } from "@/helpers/gtm-events";
import { EventBus } from "@/plugins/event-bus";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";
import gmbExternalChangesService from "@/services/externalChanges";
import ErrorHandler from "@/helpers/handlers/error";
import { FEATURE_FLAG, isFeatureFlagEnabled } from "@/helpers/posthog";

export default {
  name: "FacilityExternalChangesList",
  components: {
    IsOwnerIndicator,
    ChangeListItem,
    SectionLoader
  },

  data() {
    return {
      loaded: false,
      loading: {
        view: false
      },
      facilityData: null,
      changes: [],
      EXTERNAL_CHANGES_STATUS
    };
  },

  computed: {
    facilityDescription() {
      const storeLabel = this.facilityData.store_code
        ? ` | ${this.$t("message.common.storeCode")}: ${
            this.facilityData.store_code
          }`
        : "";
      return `${this.facilityData.formatted_address}${storeLabel}`;
    },
    isOwner() {
      return this.facilityData.facility_listings.is_owner;
    },
    facilityName() {
      return this.facilityData.name;
    }
  },

  async created() {
    this.loading.view = true;

    await this.fetchFacilityData();
    await this.fetchExternalChanges();
    this.loaded = true;
    this.loading.view = false;

    this.$gtm.trackCustomEvent({
      event: GTM_EVENT_NAME.LOCATION_DETAILS_ACTION,
      location_id: this.facilityData.id,
      action: "tab_ext_changes_list"
    });
  },

  methods: {
    async fetchExternalChanges() {
      const response = await gmbExternalChangesService.fetchExternalChanges({
        facilitiesIds: [this.$route.params.id],
        statuses: [EXTERNAL_CHANGES_STATUS.PENDING]
      });
      if (
        response.data &&
        response.data.results &&
        response.data.results.length > 0
      ) {
        this.changes = response.data.results;

        if (
          !isFeatureFlagEnabled(
            FEATURE_FLAG.SHOW_SERVICE_ITEMS_EXTERNAL_CHANGES
          )
        ) {
          this.changes = this.changes.filter(
            change => change.field.value !== "service_items"
          );
        }
      } else {
        this.changes = [];
        EventBus.$emit("facility-external-changes", false);
      }
    },

    async fetchFacilityData() {
      const data = await serviceHandler(
        facilitiesService.getFacilityData(this.$route.params.id)
      );

      if (data) {
        this.facilityData = data;
      } else {
        this.facilityData = null;
      }
    },

    async handleAction(id, status, fieldSlug) {
      this.loading.view = true;
      try {
        gtmEvent__singleExternalChangeAction({
          locationId: this.facilityData.id,
          status,
          field: fieldSlug,
          origin: "location_details"
        });

        await gmbExternalChangesService.updateExternalChanges({
          changeId: id,
          status
        });
        await this.fetchExternalChanges();
      } catch (err) {
        ErrorHandler.displayError(err);
      }

      this.loading.view = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.facility {
  display: flex;
  align-items: center;
  margin-bottom: $spacing * 3.5;

  &__content-primary {
    max-width: calc(100% - 245px);
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }

  &__address {
    margin-right: $spacing;
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.changes {
  &__list,
  &__list-header {
    padding: 0 $spacing * 4.5;
  }

  &__list-header {
    @include font-size(12px);
    opacity: 0.6;
  }

  &__list {
    border-radius: 25px;
    background-color: $light-4;
  }

  &__list-item:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }
}

.no-changes {
  @include noData(450px);

  &__description {
    @include font-size(22px);
    text-align: center;
    margin: $spacing * 5 0 $spacing * 3;
  }
}
</style>
